import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import { DistanceUnit } from "@nova/constants"
import { useTranslation } from "@nova/use-translation"
import { formatNumberWithCommas } from "@nova/util-strings"
import classNames from "classnames"
import { useEffect, useRef, useState } from "react"
import { useViewport } from "../GlobalViewState"
import { config } from "@nova/configuration/browser"
import { selectPageSize } from "plugins/PDFv2/PDFSlice"
import { useAppSelector } from "integrations/redux/hooks"

export const MapFooterScaleBar = ({ isLight }: { isLight: boolean }) => {
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }
    const size = useAppSelector(selectPageSize())
    const pageHeight = parseFloat(size.split('"')[0])
    const ref = useRef<HTMLDivElement | null>(null)
    const { t } = useTranslation()
    const [ratio, setRatio] = useState(100)
    const [distance, setDistance] = useState(100)
    const [unit, setUnit] = useState("ft")
    const view = useViewport()

    useEffect(() => {
        setTimeout(() => {
            if (!ref.current) return null

            const maxWidth = ref.current.getBoundingClientRect().width * 0.6
            const y = ref.current.getBoundingClientRect().top
            const x = ref.current.getBoundingClientRect().left
            const left = view.unproject([x, y])
            const right = view.unproject([x + maxWidth, y])
            const maxMeters = calculateTrigonmetricDistance(
                { latitude: left[1], longitude: left[0] },
                { latitude: right[1], longitude: right[0] }
            )
            let unit = "ft"
            let distance = maxMeters
            if (organizationSettingDistance === DistanceUnit.Feet) {
                distance = 3.2808 * distance
                if (distance > 5280) {
                    distance = distance / 5280
                    unit = "mi"
                }
            } else if (maxMeters >= 1000) {
                distance /= 1000
                unit = "km"
            } else {
                unit = "m"
            }

            function getDecimalRoundNum(d: number) {
                const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10))
                return Math.round(d * multiplier) / multiplier
            }

            function getRoundNum(num: number) {
                const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1)
                let d = num / pow10

                d =
                    d >= 10
                        ? 10
                        : d >= 5
                          ? 5
                          : d >= 3
                            ? 3
                            : d >= 2
                              ? 2
                              : d >= 1
                                ? 1
                                : getDecimalRoundNum(d)

                return pow10 * d
            }
            const d = getRoundNum(distance)
            const ratio = getRoundNum(distance) / distance
            setRatio(ratio)
            setDistance(d)
            setUnit(unit)
        }, 0)
    }, [view, unit, ref, pageHeight, organizationSettingDistance])

    return (
        <div ref={ref} className="w-full mb-1">
            <div className="flex items-center gap-1">
                <div className="w-3/5 flex items-center justify-end">
                    <div
                        className={classNames("flex border", {
                            "border-black-300": isLight,
                            "border-white-300": !isLight,
                        })}
                        style={{ width: `${ratio * 100}%` }}
                    >
                        <div className="flex-1 bg-gray-0 h-0.5" />
                        <div className="flex-1 bg-gray-900 h-0.5" />
                    </div>
                </div>
                <small
                    className={classNames("w-2/5 mono", {
                        "text-gray-800": isLight,
                        "text-gray-0": !isLight,
                    })}
                    data-tid="zoom-level"
                >
                    {t(`${formatNumberWithCommas(distance)} ${unit}`)}
                </small>
            </div>
        </div>
    )
}
